import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowRight, ArrowLeft, CalendarCheck, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui-bits";
import { COMPANY_SIZES } from "@/lib/constants";
import { toast } from "sonner";

const EMPTY = { name: "", work_email: "", company: "", team_size: "", message: "" };

export default function BookDemo() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/demo-requests", form);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err, t("demo.toastError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="book-demo-page">
      <MarketingHeader />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-16 md:py-20">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="demo-back">
          <ArrowLeft size={15} /> {t("demo.backHome")}
        </Link>

        {done ? (
          <div data-testid="demo-success" className="card-milled rounded-2xl p-8 md:p-10 text-center">
            <span className="grid place-items-center w-14 h-14 rounded-full bg-success/10 text-success mx-auto mb-5"><CheckCircle2 size={28} /></span>
            <h1 className="text-2xl font-semibold tracking-tight">{t("demo.successTitle")}</h1>
            <p className="text-muted-foreground mt-2 leading-relaxed max-w-md mx-auto">{t("demo.successBody")}</p>
            <div className="flex items-center justify-center gap-3 mt-7">
              <Link to="/"><Button variant="outline" className="rounded-lg bg-card">{t("demo.backHome")}</Button></Link>
              <Button className="rounded-lg" data-testid="demo-another" onClick={() => { setDone(false); setForm(EMPTY); }}>{t("demo.another")}</Button>
            </div>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-primary">
              <CalendarCheck size={14} /> {t("demo.eyebrow")}
            </span>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-4">{t("demo.title")}</h1>
            <p className="text-muted-foreground mt-3 leading-relaxed">{t("demo.subtitle")}</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="demo-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("demo.nameLabel")}</Label>
                  <Input data-testid="demo-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("demo.namePlaceholder")} className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("demo.emailLabel")}</Label>
                  <Input data-testid="demo-email" type="email" required value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} placeholder={t("demo.emailPlaceholder")} className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("demo.companyLabel")}</Label>
                  <Input data-testid="demo-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("demo.companyPlaceholder")} className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">{t("demo.teamSizeLabel")}</Label>
                  <Select value={form.team_size} onValueChange={(v) => setForm({ ...form, team_size: v })}>
                    <SelectTrigger data-testid="demo-team-size" className="h-11 rounded-lg"><SelectValue placeholder={t("demo.teamSizePlaceholder")} /></SelectTrigger>
                    <SelectContent>{COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t("demo.messageLabel")}</Label>
                <Textarea data-testid="demo-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={1000} placeholder={t("demo.messagePlaceholder")} className="rounded-lg min-h-[110px]" />
              </div>
              <Button data-testid="demo-submit" type="submit" disabled={loading} className="w-full h-11 rounded-lg active:scale-[0.98] transition-transform">
                {loading ? <Spinner /> : <>{t("demo.submit")} <ArrowRight size={16} className="ml-1" /></>}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                <Trans i18nKey="demo.privacyNote" components={{ lnk: <Link to="/privacy" className="text-primary hover:underline" /> }} />
              </p>
            </form>
          </>
        )}
      </main>
      <MarketingFooter />
    </div>
  );
}
